/**
 * Quota-safe sessionStorage adapter for Zustand persist.
 *
 * sessionStorage is ~5 MB per origin. Workbench persist keys share that
 * budget (`workbench-graph`, `workbench-transactions`, `workbench-stash`,
 * `workbench_draft`). An uncaught QuotaExceededError on setItem bubbles
 * through Zustand persist into React Router and crashes the page:
 *
 *   "Failed to execute 'setItem' on 'Storage': Setting the value of
 *    'workbench-transactions' exceeded the quota."
 *
 * Persistence is best-effort. In-memory state is kept; Save Snapshot
 * remains the durable path. On quota we:
 *   1. Drop the key being written (stale oversized snapshot).
 *   2. Evict other expendable workbench keys and retry once.
 *   3. Warn once per key; never throw.
 */

export const WORKBENCH_QUOTA_EVICT_KEYS = [
    'workbench-transactions',
    'workbench_draft',
    'workbench-stash',
    // Restorable conveniences: the graph itself is worth more than these.
    'workbench-results',
    'workbench-view',
] as const;

/** Per-key character cap so one persist blob cannot consume the whole origin budget. */
export const MAX_PERSIST_CHARS = 2_000_000;

export interface QuotaSafeStorage {
    getItem: (name: string) => string | null;
    setItem: (name: string, value: string) => void;
    removeItem: (name: string) => void;
}

export interface QuotaSafeOptions {
    warnKey?: string;
    warnMessage?: string;
    /** Extra keys to drop when the write still does not fit after removing `name`. */
    evictKeys?: readonly string[];
    maxChars?: number;
}

export function isQuotaExceeded(err: unknown): boolean {
    const e = err as { name?: string; code?: number; message?: string } | null;
    if (!e) return false;
    return (
        e.name === 'QuotaExceededError' ||
        e.code === 22 ||
        e.code === 1014 ||
        e.code === -2147024882 ||
        (typeof e.message === 'string' && e.message.toLowerCase().includes('quota'))
    );
}

const warnedInMemory = new Set<string>();

/** Test-only: quota warnings are once-per-session via this set. */
export function resetQuotaWarningsForTests(): void {
    warnedInMemory.clear();
}

function warnOnce(storage: QuotaSafeStorage, warnKey: string, message: string): void {
    if (warnedInMemory.has(warnKey)) return;
    try {
        if (storage.getItem(warnKey)) {
            warnedInMemory.add(warnKey);
            return;
        }
        storage.setItem(warnKey, '1');
    } catch {
        /* ignore — even the warn flag may not fit */
    }
    warnedInMemory.add(warnKey);
    // eslint-disable-next-line no-console
    console.warn(message);
}

function safeRemove(storage: QuotaSafeStorage, name: string): void {
    try {
        storage.removeItem(name);
    } catch {
        /* ignore */
    }
}

export function createQuotaSafeStorage(
    getStorage: () => QuotaSafeStorage,
    options: QuotaSafeOptions = {},
): QuotaSafeStorage {
    const warnKey = options.warnKey ?? '__workbench_quota_warned__';
    const warnMessage = options.warnMessage
        ?? '[workbench] sessionStorage quota exceeded — session will not persist across reloads. Use Save Snapshot to keep this investigation.';
    const evictKeys = options.evictKeys ?? WORKBENCH_QUOTA_EVICT_KEYS;
    const maxChars = options.maxChars ?? MAX_PERSIST_CHARS;

    const storage = (): QuotaSafeStorage => getStorage();

    return {
        getItem: (name: string) => storage().getItem(name),
        setItem: (name: string, value: string) => {
            const store = storage();
            if (typeof value === 'string' && value.length > maxChars) {
                safeRemove(store, name);
                warnOnce(
                    store,
                    warnKey,
                    `${warnMessage} (dropped ${name}: ${value.length} chars exceeds ${maxChars} cap)`,
                );
                return;
            }
            try {
                store.setItem(name, value);
                return;
            } catch (err: unknown) {
                if (!isQuotaExceeded(err)) throw err;
            }

            safeRemove(store, name);
            for (const key of evictKeys) {
                if (key === name) continue;
                safeRemove(store, key);
            }

            try {
                store.setItem(name, value);
                return;
            } catch (err: unknown) {
                if (!isQuotaExceeded(err)) throw err;
            }

            safeRemove(store, name);
            warnOnce(store, warnKey, warnMessage);
        },
        removeItem: (name: string) => storage().removeItem(name),
    };
}

export function createQuotaSafeSessionStorage(options: QuotaSafeOptions = {}): QuotaSafeStorage {
    return createQuotaSafeStorage(() => sessionStorage, options);
}
